import {
  ActionIcon,
  Button,
  Flex,
  Group,
  HoverCard,
  Text,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import {
  copyWorkflow,
  deleteWorkflow,
  editWorkflow,
  getWorkflows,
} from "../../../../../api/WorkflowAPI";
import { useGridApiRef } from "@mui/x-data-grid-pro";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../../../../../stores/LanguageStore";
import { useCallback, useEffect, useMemo, useState } from "react";
import numberColumn from "../../../../../layouts/Table/columns/numberColumn";
import { nameColumn } from "../../../../../layouts/Table/columns/nameColumn";
import dateColumn from "../../../../../layouts/Table/columns/dateColumn";
import { sharingColumn } from "../../../../../layouts/Table/columns/sharingColumn";
import SelectionDeleter from "../../../../../components/TableComponents/SelectionDeleter";
import { useListingLoader } from "../../../../../components/hooks/ListingHooks";
import MainTable from "../../../../../layouts/Table/MainTable";
import {
  IconArchive,
  IconEdit,
  IconEye,
  IconPlus,
  IconSend,
  IconUserStar,
} from "@tabler/icons-react";
import { tagsColumn } from "../../../../../layouts/Table/columns/tagsColumn";
import SelectionSharer from "../../../../../components/TableComponents/SelectionSharer";
import RequiredActions from "../../../account/RequiredActions/RequiredActions";
import WorkflowActions from "../components/WorkflowActions";
import SelectionMover from "../../../../../components/TableComponents/SelectionMover";
import FoldersDropdown from "../../../lists/ListingWithContent/contacts/FoldersDropdown";
import { booleanColumn } from "../../../../../layouts/Table/columns/booleanColumn";
import NoItemsModal from "../../../../../components/TableComponents/NoItemsModal";
import WorkflowPreviewHover from "../../../workflows/listing/WorkflowPreviewHover";
import SelectionDuplicator from "../../../../../components/TableComponents/SelectionDuplicator";
import { useCampaignStore } from "../../../../../stores/CampaignStore";
import { getNodeDetails, getTypeIcon } from "../../../workflows/CreateCampaign/flow/flowSettings";

const WorkflowListing = () => {
  const lang = useLanguage((s) => s.language);
  const apiRef = useGridApiRef();
  const [highlight, setHighlight] = useState(null);
  const [hasQuery, setHasQuery] = useState(false);
  const filterType = useCampaignStore((s) => s.workflowType);
  const { folderId } = useParams();

  const columns = useMemo(
    () => [
      numberColumn(),
      nameColumn({
        apiRef,
        width: 340,
        editFunction: () => editWorkflow,
        disableLink: true,
        handleClick: () => { },
        leftSection: (row) => {
          if (row?.image_url) {
            return <WorkflowPreviewHover row={row} />;
          }
          if (row?.nurturing) {
            return (
              <Tooltip label={"Nurturing"}>
                <ThemeIcon color="pink">
                  <IconUserStar size={18} />
                </ThemeIcon>
              </Tooltip>
            );
          }
          return null;
        },
        bottomSection: (row) => {
          return (
            <Group gap={4}>
              {
                ["has_email_step", "has_linkedin_invitation_step", "has_linkedin_message_step", "has_linkedin_visit_step", "has_sms_step", "has_smv_step"].map((type) => {
                  if (row[type]) {
                    let type_key = type.replace("has_", "").replace("_step", "");
                    return (
                      <Tooltip key={type} label={lang.workflows.columns[type]}>
                        {getTypeIcon(type_key, false, 24, {
                          size: 20,
                          opacity: 1,
                          variant: "subtle",
                          mt: 0,
                          color: getNodeDetails(type_key)?.color || "secondary",
                        })}
                      </Tooltip>
                    );
                  }
                  return null;
                })
              }
            </Group>
          )
        }
      }),
      booleanColumn({
        field: "programmed",
        headerName: lang.workflows.columns.programmed.name,
        falseLabel: lang.workflows.columns.programmed.false,
        trueLabel: lang.workflows.columns.programmed.true,
        width: 130,
        noColor: { error: true },
      }),
      booleanColumn({
        field: "archived",
        headerName: lang.workflows.columns.archived.name,
        falseLabel: lang.workflows.columns.archived.false,
        trueLabel: lang.workflows.columns.archived.true,
        width: 130,
        noColor: { error: true },
        inverse: true,
      }),
      tagsColumn({ apiRef, editFunction: () => editWorkflow }),
      sharingColumn({
        apiRef,
        editFunction: () => editWorkflow,
        allowResellerCustomers: true,
      }),
      dateColumn({
        field: "updated_on",
        headerName: lang.workflows.columns.updated_on,
      }),
      dateColumn(),
    ],
    [apiRef, lang]
  );

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);

  const toolbarActions = useCallback(
    ({ selectedIds, handleClose }) => {
      return (
        <>
          <SelectionDuplicator
            getHandler={(current) => copyWorkflow}
            setTableLoading={setLoading}
            ids={selectedIds}
            rows={rows}
            closeMenu={handleClose}
            apiRef={apiRef}
          />

          <SelectionMover
            handleEdit={editWorkflow}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
            folderType={"Workflow"}
          />

          <SelectionSharer
            handleShare={editWorkflow}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
            rows={rows}
          />

          <SelectionDeleter
            handleDelete={({ id }) => editWorkflow({ id: id, archived: true })}
            apiRef={apiRef}
            setTableLoading={setLoading}
            onFinish={() =>
              setTimeout(() => {
                setLoading(true);

                apiRef?.current?.setRowSelectionModel([]);
              }, 100)
            }
            ids={selectedIds}
            tooltip={lang.statistics.archive.label}
            icon={<IconArchive size={18} />}
            buttonTooltip={lang.campaigns.archive_title}
            warningText={
              <Text size="sm">{lang.campaigns.archive_warning}</Text>
            }
            title={
              <Text size="md" truncate="end">
                {lang.campaigns.archive_title1}{" "}
                <b>
                  {" "}
                  {selectedIds.length} {lang.campaigns.archive_title2}{" "}
                </b>
              </Text>
            }
            submitButtonText={lang.statistics.archive.dialog.submit_button}
            disableRowDelete
          />

          <SelectionDeleter
            handleDelete={deleteWorkflow}
            apiRef={apiRef}
            setTableLoading={setLoading}
            ids={selectedIds}
            rows={rows}
            canRowsDelete={() => {
              return (
                selectedIds.filter(
                  (x) => rows.find((r) => r.id === x).programmed === true
                ).length > 0
              );
            }}
            disabledMessage={lang.workflows.cant_delete_workflow_programmed}
          />
        </>
      );
    },
    [apiRef, lang, rows]
  );

  const actionsCell = useCallback(
    ({ row, value }) => {
      return (
        <Group wrap="nowrap" gap={4}>
          <Tooltip label={lang.global.edit}>
            <ActionIcon
              variant="subtle"
              component={Link}
              to={`/campaign/create/workflow/${row.id}`}
            >
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>

          <WorkflowActions
            apiRef={apiRef}
            current={row}
            onFinish={() => setLoading(true)}
          />
          {/* <DuplicateRessource
            current={row}
            onFinish={() => setLoading(true)}
            closeMenu={() => {}}
            handler={copyWorkflow}
            responseField={"workflow_id"}
            isIcon
          />
          <ItemDeleter
            title_bold={`${row.name} #${row.id}`}
            handleDelete={deleteWorkflow}
            deletedMessage={lang.workflows.delete_success}
            id={row.id}
            disabled={
              !row?.delete_permission && !row?.permission?.delete_permission
            }
            apiRef={apiRef}
          /> */}
        </Group>
      );
    },
    [apiRef, lang]
  );

  const navigate = useNavigate();
  const handleRowClick = ({ row }) => {
    navigate(`/campaign/create/workflow/${row.id}`);
  };

  useEffect(() => {
    if (folderId !== "none") setLoading(true);
  }, [folderId]);

  useListingLoader({
    loading,
    setLoading,
    listGetter: getWorkflows,
    getRows: (result) => {
      return result.sort((a, b) => a.archived - b.archived);
    },
    responseField: "workflows",
    setRows,
    folderId,
  });

  return (
    <>
      {folderId && (
        <Group w={400} mt={64} mb={-47} pos={"relative"} style={{ zIndex: 10 }}>
          <FoldersDropdown
            type="Workflow"
            getClickLink={(id) =>
              `/statistics/campaigns/workflows/folder/${id}/workflow/none`
            }
            getDeletedLink={() => `/statistics/campaigns/workflows/folder`}
          />
        </Group>
      )}
      <Flex flex={"1 1 auto"} h={0} pos={"relative"}>
        <NoItemsModal
          rows={rows}
          loading={loading}
          text={
            <Text size="xl" mb={32}>
              {lang.campaigns.no_workflow_found}
            </Text>
          }
          button={
            <Button
              leftSection={<IconPlus size={18} />}
              radius={"md"}
              color="primary"
              component={Link}
              to={"/campaign/create"}
              size="md"
            >
              {lang.campaigns.create_campaign}
            </Button>
          }
        />
        <MainTable
          apiRef={apiRef}
          columns={columns}
          rows={rows.filter(x => {
            if (filterType.length === 0) return true;
            if (filterType.includes("email") && x.has_email_step) return true;
            if (filterType.includes("linkedin") && (x.has_linkedin_invitation_step || x.has_linkedin_message_step || x.has_linkedin_visit_step)) return true;
            if (filterType.includes("sms") && x.has_sms_step) return true;
            if (filterType.includes("smv") && x.has_smv_step) return true;
            return false;
          })}
          loading={loading}
          setLoading={setLoading}
          id={"workflows-table"}
          onRowClick={handleRowClick}
          toolbarActions={toolbarActions}
          actionsCell={actionsCell}
          actionsCellWidth={75}
          rowHeightMultiplier={1.2}
          hasFolder
          pageName={"workflows"}
          pageOrderName={"workflows_order"}
          defaultHighlight={highlight}
          setDefaultHighlight={setHighlight}
          setHasQuery={setHasQuery}
          hasQuery={hasQuery}
          whiteButtons
          tableSx={{
            "& .table-search": {
              marginLeft: "672px",
              width: "180px !important",
            },
          }}
        />
      </Flex>

      <RequiredActions />
    </>
  );
};

export default WorkflowListing;
